import { TabsProps as AntdTabsProps } from "antd/es";
import { Guid } from "guid-typescript";
import _, { isEmpty } from "lodash";
import "rc-tabs/assets/index.css";
import ScrollableInkTabBar from "rc-tabs/es/ScrollableInkTabBar";
import TabContent from "rc-tabs/es/TabContent";
import Tabs from "rc-tabs/es/Tabs";
import React, { PureComponent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { CommonProps } from "../common/common-props";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: any;
interface TabProps extends AntdTabsProps {
  activeKey: string;
  defaultActiveKey: string;
  onChange?: (activeKey: string) => any;
  value?: string | number;
  selectedTabColor?: string;
  className?: string;
  children?: any;
  kuikaid?: string;
}

interface TabState {
  designTimeValue: string;
}

class Tab extends PureComponent<TabProps & CommonProps, TabState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`ktab_${this.uniqueKey}`);

  shouldPreventOnChangeFunction: boolean = false;

  constructor(props: TabProps) {
    super(props);
    this.state = {
      designTimeValue: ""
    };
  }

  initializeWindowApi = () => {
    window.kuika ??= {};
    window.kuika.tabs ??= {};
    window.kuika.tabs[this.props.kuikaid] ??= {};
    window.kuika.tabs[this.props.kuikaid].setTab = (index: number) => {
      this.handleOnChangeTab(this.props.children[index].key, true);
    };
  };

  destroyWindowApi = () => {
    delete window.kuika.tabs[this.props.kuikaid];
  };

  componentDidMount() {
    if (window.kuika?.isDesignTime) {
      this.initializeWindowApi();
    }
    this.setDynamicStyle();

    const children = this.getChildren();
    if (children.length) {
      let activeIndex = 0;

      if (window.kuika?.dashboardState === DashboardState.design) {
        const lastActiveTab = window.kuika[`${this.props.kuikaid}_lastActiveTab`];
        if (lastActiveTab) {
          activeIndex = children.findIndex(
            (tabItem) => tabItem.key === lastActiveTab || tabItem.props?.kuikaid === lastActiveTab
          );
          if (activeIndex === -1) activeIndex = 0;
          this.setState({ designTimeValue: children[activeIndex].key });
        } else {
          activeIndex = children.findIndex(
            (tabItem) =>
              tabItem.key === this.state.designTimeValue || tabItem.props?.kuikaid === this.state.designTimeValue
          );
          if (activeIndex === -1) activeIndex = 0;
          this.setState({ designTimeValue: children[activeIndex].key });
        }
      } else if (this.props.value) {
        activeIndex = children.findIndex(
          (tabItem) => tabItem.key === this.props.value || tabItem.props?.kuikaid === this.props.value
        );
        if (activeIndex === -1) activeIndex = 0;
      }

      if (children[activeIndex].props?.onActive) {
        children[activeIndex].props.onActive();
      }
    }
  }

  componentWillUnmount() {
    if (window.kuika?.isDesignTime) {
      this.destroyWindowApi();
    }
    this.style.removeStyle();
  }

  componentDidUpdate(prevProps: Readonly<TabProps & CommonProps>, prevState: Readonly<TabState>, snapshot?: any): void {
    if (window.kuika?.isDesignTime) return;

    if (this.props.value !== prevProps.value) {
      if (this.shouldPreventOnChangeFunction) {
        if (this.props.onChange) this.shouldPreventOnChangeFunction = false;
      } else {
        if (this.props.onChange) this.props.onChange(`${this.props.value}`);
        this.handleTabItemOnActive(this.props.value);
      }
    }

    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  handleOnChangeTab = (activeTabKey: any, setState: boolean) => {
    if (window.kuika?.dashboardState === DashboardState.design) {
      this.setState({ designTimeValue: activeTabKey });
      window.kuika ??= {};
      window.kuika[`${this.props.kuikaid}_lastActiveTab`] = activeTabKey;
      return;
    }

    if (this.props.onChange) {
      this.shouldPreventOnChangeFunction = true;
      this.props.onChange(activeTabKey);
    }

    this.handleTabItemOnActive(activeTabKey);
  };

  handleTabItemOnActive = (activeTabKey) => {
    const activeChild = this.props.children?.find(
      (tabItem) => (tabItem.key === activeTabKey || tabItem.props?.kuikaid === activeTabKey) && tabItem.props?.onActive
    );
    if (!activeChild) return;

    activeChild.props.onActive();
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";

    const { selectedTabColor, tabPosition } = this.props;
    const { textAlign }: any = this.props.style;
    if (selectedTabColor) {
      result += `.${className.trim()} .rc-tabs-tab-active, .rc-tabs-tab-active:active, .rc-tabs-tab-active:hover, .rc-tabs-tab-active:focus, .rc-tabs-tab:hover, .rc-tabs-tab:focus, .rc-tabs-tab:active {
          color: ${this.props.selectedTabColor} !important;
        }`;
      result += `.${className.trim()} .rc-tabs-tab-disabled, .rc-tabs-tab-disabled:hover, .rc-tabs-tab-disabled:active, .rc-tabs-tab-disabled:focus {
          color: #ccc !important;
        }`;
      result += `.${className.trim()} .rc-tabs-tab {
          transition: none !important;
        }`;
      result += `.${className.trim()} .rc-tabs-ink-bar {
          background: ${this.props.selectedTabColor};
        }`;
    }
    result += `.${className.trim()} .rc-tabs-tabpane {
        padding: 0px !important;
        overflow: hidden !important;
    }`;
    result += `.${className.trim()} .rc-tabs-tab {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }`;

    result += `.${className.trim()} {
      display: block !important;
      align-items: unset !important;
    }`;

    result += `.${className.trim()} .rc-tabs-bar {
      margin: 0 0 16px !important;
      border-color: transparent !important;
    }`;

    result += `.${className.trim()}.rc-tabs-left .rc-tabs-tab {
      padding-right: 8px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right .rc-tabs-tab {
      padding-left: 8px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-left .rc-tabs-bar {
      margin-right: 16px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right .rc-tabs-bar {
      margin-left: 16px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-bottom .rc-tabs-bar {
      margin-top: 16px !important;
    }`;

    result += `.${className.trim()} .rc-tabs-tabpane-active {
      padding: 0px !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right .rc-tabs-nav-scroll,
    .${className.trim()}.rc-tabs-left .rc-tabs-nav-scroll {
      height: auto;
    }`;

    result += `.${className.trim()}.rc-tabs-left,
    .${className.trim()}.rc-tabs-right {
      display: flex !important;
    }`;

    result += `.${className.trim()}.rc-tabs-right {
      flex-direction: row-reverse;
    }`;

    result += `.${className.trim()} .rc-tabs-content.rc-tabs-content-animated {
      flex: 1;
    }`;

    if (textAlign && textAlign !== "-webkit-left") {
      result += `.${className.trim()}.rc-tabs-top .rc-tabs-nav-container,
      .${className.trim()}.rc-tabs-bottom .rc-tabs-nav-container {
        text-align: ${textAlign} !important;
      }`;

      result += `.${className.trim()} .rc-tabs-nav-wrap {
        justify-content: ${textAlign === "-webkit-right" ? "flex-end" : "center"} !important;
        display: flex !important;
      }`;

      result += `.${className.trim()} .rc-tabs-nav {
        display: inline-flex !important;
        float: none !important;
        ${textAlign === "center" ? "margin: 0 auto !important;" : ""}
        ${textAlign === "right" ? "margin-left: auto !important;" : ""}
      }`;

      result += `.${className.trim()}.rc-tabs-left .rc-tabs-nav-container,
      .${className.trim()}.rc-tabs-right .rc-tabs-nav-container {
        align-items: ${textAlign === "-webkit-right" ? "flex-end" : "center"};
      }`;
    }

    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.uniqueKey) {
      return result;
    }
    result = `${result} ktab_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getProps = () => {
    let props = {} as any;
    props = _.clone(this.props);
    if (props.activeKey) {
      delete props.activeKey;
    }
    if (props.defaultActiveKey) {
      delete props.defaultActiveKey;
    }
    if (props.onChange) {
      delete props.onChange;
    }
    if (props.children) {
      delete props.children;
    }
    if (props.selectedTabColor) {
      delete props.selectedTabColor;
    }

    props.tabBarPosition = props.tabPosition || "top";
    delete props.tabPosition;

    return props;
  };

  getChildren() {
    if (!this.props.children.length) {
      if (this.props.children.props.visibility !== "hidden") {
        return [this.props.children];
      }
      return [];
    }

    return this.props.children
      .filter((child) => child.props.visibility !== "hidden")
      .map((child) => {
        return React.cloneElement(child, { disabled: child.props.editability === "disabled" });
      });
  }

  getActiveKey = () => {
    if (window.kuika?.dashboardState === DashboardState.design) {
      return this.state.designTimeValue;
    }

    let activeKey = this.props.value;
    let keyProp = "id";
    if (window.kuika?.dashboardState === 1) {
      activeKey = this.props.activeKey;
      keyProp = "kuikaid";
    }

    if (isEmpty(activeKey) && this.props.children && this.props.children[0] && this.props.children[0].props) {
      if (this.props.children[0].props.visibility === "hidden") {
        const nextTab = this.props.children.find((child) => child.props.visibility !== "hidden");
        if (nextTab) {
          activeKey = nextTab.props[keyProp];
        }
      } else if (this.props.children[0].props[keyProp]) {
        activeKey = this.props.children[0].props[keyProp];
      }
    } else if (isEmpty(activeKey) && this.props.children) {
      activeKey = this.props.children.props[keyProp];
    }

    return activeKey;
  };

  render(): ReactNode {
    let activeKey = this.getActiveKey();
    return (
      <Tabs
        animated={false}
        {...this.getProps()}
        className={this.getClassName()}
        onChange={this.handleOnChangeTab}
        activeKey={activeKey}
        defaultActiveKey={this.props.defaultActiveKey}
        renderTabBar={() => <ScrollableInkTabBar />}
        renderTabContent={() => <TabContent />}
      >
        {this.getChildren()}
      </Tabs>
    );
  }
}

const tab = withCommonEvents(Tab);
export { tab as Tab };
